import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import i18n from "../../i18n";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import { useGetFRLogServerUrl } from "../../hooks/common/query";
import Loader from "../loader";
import {
  useSyncFRLogServerUrl,
  useUpdateFRLogServerUrl,
} from "../../hooks/common/mutation";
import toast from "react-hot-toast";

export const LogsServerFRConfigurationsCardSchema = Yup.object({
  deviceLogsServer: Yup.string().required(
    i18n.language === "en"
      ? "Device Logs Server is required."
      : "يجب أن يكون خادم سجلات الجهاز مطلوبًا."
  ),
});

interface FormData {
  deviceLogsServer: string;
}

interface LogsServerFRConfigurationsCardProps {
  selectedDeviceIPAddresses: string[];
}

const LogsServerFRConfigurationsCard = ({
  selectedDeviceIPAddresses,
}: LogsServerFRConfigurationsCardProps) => {
  const { t } = useTranslation();

  const { data: frLogServerUrl, isPending: isFRLogServerUrlLoading } =
    useGetFRLogServerUrl();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useFormWithSchema(LogsServerFRConfigurationsCardSchema, {
    defaultValues: {
      deviceLogsServer: "http://",
    },
  });

  const {
    mutateAsync: UpdateFRLogServerUrl,
    isPending: isUpdateFRLogServerUrlLoading,
  } = useUpdateFRLogServerUrl();

  const {
    mutateAsync: SyncFRLogServerUrl,
    isPending: isSyncFRLogServerUrlLoading,
  } = useSyncFRLogServerUrl();

  useEffect(() => {
    if (frLogServerUrl) {
      reset({
        deviceLogsServer: frLogServerUrl.logServerUrl,
      });
    }
  }, [frLogServerUrl, reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const body = {
          logServerUrl: data.deviceLogsServer,
        };
        await UpdateFRLogServerUrl(body);

        toast.success(t("frLogServerUrlUpdatedSuccessfully"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }

      if (selectedDeviceIPAddresses.length === 0) {
        toast.error(t("kindlySelectDeviceToSync"));
      } else {
        try {
          const body = {
            logServerUrl: data.deviceLogsServer,
            fripAddresses: selectedDeviceIPAddresses,
          };

          await SyncFRLogServerUrl(body);

          toast.success(t("frLogServerUrlSyncedSuccessfully"));
        } catch (error) {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [SyncFRLogServerUrl, selectedDeviceIPAddresses, UpdateFRLogServerUrl, t]
  );

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <h2 className="text-lg lg:text-xl font-medium">
        {t("logsServerConfiguration")}
      </h2>

      {isFRLogServerUrlLoading ? (
        <Loader />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid sm:grid-cols-2 gap-2 md:gap-4 items-end"
        >
          <Controller
            name="deviceLogsServer"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("deviceLogsServer")}
                onChange={onChange}
                value={value || ""}
                error={errors?.[name]?.message}
                required
              />
            )}
          />

          <Button
            isLoading={
              isUpdateFRLogServerUrlLoading || isSyncFRLogServerUrlLoading
            }
            className="max-w-max !py-[14px] !px-9"
          >
            {t("pushSync")}
          </Button>
        </form>
      )}
    </div>
  );
};

export default React.memo(LogsServerFRConfigurationsCard);
